import { Fragment, useEffect, useState } from 'react'
import { fetchData } from '../../helpers/fetch'
import { type_WISHLIST_DASHBOARD_STATE, typeProduct, typeOptionsWithValues, OmneoList } from '../types'
import { LoaderSpin } from './LoaderSpin'
import { Modal } from './Modal'
import ProductVariant from './ProductVariant'
import { WishlistListPickerContent } from './WishlistSlideout'
import { WishlistButton } from './WishlistButton'
import { wishlistProviderService } from '../helpers/provider.service'
import classNames from 'classnames'

type props = {
  productId: number
  productHandle: string | null
  variantSelectedId?: number
  templateSuffix?: string
  productPersonalisedPrice?: string
  openSource?: 'pdp' | 'plp' | 'cart'
}

declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE
declare var Shopify: any
declare global {
  interface Window {
    backing_product: {
      first: string
      second: string
    }
  }
}

type WishlistCartPropertyItem = {
  form_type: string
  id: string
  properties: Record<string, any>
  quantity: number
  utf8: string
  'product-id'?: string
  'option-1'?: string
  'option-2'?: string
  pair?: string
}

export function WishlistProductSlideout({
  productId,
  productHandle,
  variantSelectedId,
  templateSuffix = '',
  productPersonalisedPrice = '0',
  openSource = 'plp'
}: props) {
  if (!productId || !productHandle) return null

  return (
    <Modal
      id="wishlist-product-slideout"
      heading={openSource === 'pdp' || openSource === 'cart' ? wishlistDashboardState.chooseWishlist : wishlistDashboardState.wishlist}
      content={
        <RenderContent
          productId={productId}
          productHandle={productHandle}
          variantSelectedId={variantSelectedId}
          templateSuffix={templateSuffix}
          productPersonalisedPrice={productPersonalisedPrice}
          openSource={openSource}
        />
      }
      type="slideout"
      className="modal-slideout-product"
      renderOnOpen
    />
  )
}

function RenderContent({ productId, productHandle, variantSelectedId, templateSuffix = '', productPersonalisedPrice = '0', openSource = 'plp' }: props) {
  const [product, setProduct] = useState<typeProduct>()
  const [isLoading, setIsLoading] = useState(false)
  const [currentHandle, setCurrentHandle] = useState(productHandle)
  const [options, setOptions] = useState<typeOptionsWithValues[]>([])
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({})

  const fetchProduct = async () => {
    setIsLoading(true)
    try {
      const product = await fetchData('/products/' + currentHandle + '?view=ajax')
      setProduct(product)

      const cartilage = 'cartilage'
      if (product.handle.includes(cartilage)) {
        const platinum = 'platinum'
        const yellowGold = 'yellow-gold'
        const currentMetall = product.handle.includes(platinum) ? platinum : product.handle.includes(yellowGold) ? yellowGold : undefined
        const handle = currentMetall === yellowGold ? window.backing_product.first : window.backing_product.second
        const [cartilageProduct, lists]: [typeProduct, Array<OmneoList>] = await Promise.all([
          fetchData('/products/' + handle + '?view=ajax'),
          wishlistProviderService.getLists()
        ])
        const options = cartilageProduct.options_with_values.filter((option) => option.values.length > 1)

        let savedProperties = {}
        lists.forEach((list: OmneoList) => {
          list.items.forEach((item) => {
            const meta = item.meta
            if (product?.id === meta.productId) {
              savedProperties = meta.properties[0].properties
            }
          })
        })

        setOptions(options)

        if (Object.keys(savedProperties).length > 0) {
          setSelectedOptions(savedProperties)
        } else if (options.length) {
          setSelectedOptions({
            [options[0].name]: options[0].values[0]
          })
        }
      }
    } catch (error) {
      console.error('error', error)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (!currentHandle) {
      return
    }
    fetchProduct().then()
  }, [currentHandle])

  useEffect(() => {
    if (!productHandle) {
      return
    }
    setCurrentHandle(productHandle)
  }, [productHandle])

  return (
    productId &&
    productHandle && (
      <div className={classNames('product-slideout w-full', (openSource === 'pdp' || openSource === 'cart') && 'pt-20')}>
        {isLoading && (
          <div>
            <LoaderSpin />
          </div>
        )}
        {!isLoading && !product && <div>Product not found</div>}
        {!isLoading && product && (
          <RenderProduct
            product={product}
            setCurrentHandle={setCurrentHandle}
            options={options}
            defaultSelectedOptions={selectedOptions}
            variantSelectedId={variantSelectedId}
            templateSuffix={templateSuffix}
            productPersonalisedPrice={productPersonalisedPrice}
            openSource={openSource}
          />
        )}
      </div>
    )
  )
}

function RenderProduct({
  product,
  setCurrentHandle,
  options,
  defaultSelectedOptions,
  variantSelectedId,
  templateSuffix = '',
  productPersonalisedPrice = '0',
  openSource = 'plp'
}: {
  product: typeProduct
  setCurrentHandle: React.Dispatch<React.SetStateAction<string | null>>
  options: typeOptionsWithValues[]
  defaultSelectedOptions: { [key: string]: string }
  variantSelectedId?: number
  templateSuffix?: string
  productPersonalisedPrice?: string
  openSource?: 'pdp' | 'plp' | 'cart'
}) {
  const [currentVariant, setCurrentVariant] = useState(product?.variants.find((variant) => variant.id === variantSelectedId) || product?.variants[0])
  const [propertiesLocal, setPropertiesLocal] = useState<any>([])
  const [showWishlistSelector, setShowWishlistSelector] = useState(openSource === 'pdp' || openSource === 'cart')
  const colours = product?.metafields?.colours
  const hasPair = product.tags.includes('Template_Same Pair') // "Template_Same Pair"
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>(defaultSelectedOptions)
  // Both PDP and cart open straight into the list picker, skipping the variant-select step PLP cards use
  const isPdpSource = openSource === 'pdp' || openSource === 'cart'

  useEffect(() => {
    const nextVariant = product?.variants.find((variant) => variant.id === variantSelectedId) || product?.variants[0]
    if (nextVariant) {
      setCurrentVariant(nextVariant)
    }
  }, [product, variantSelectedId])

  const handlePrice = () => {
    if (hasPair && currentVariant.option2 === 'Matched Pair') {
      return `${((currentVariant.price * 2) / 100).toLocaleString()} ${Shopify.currency.active}`
    }
    return `${(currentVariant.price / 100).toLocaleString()} ${Shopify.currency.active}`
  }

  useEffect(() => {
    const props: WishlistCartPropertyItem[] = [
      {
        form_type: 'product',
        id: currentVariant.id.toString(),
        properties: selectedOptions,
        quantity: hasPair && currentVariant.option2 === 'Matched Pair' ? 2 : 1,
        utf8: '✓'
      }
    ]
    props[0]['product-id'] = product.id.toString()
    if (!hasPair) {
      if (currentVariant.option2) {
        props[0]['option-1'] = currentVariant.option2
      }
      if (currentVariant.option3 && product.options[2] !== 'Stone') {
        props[0]['option-2'] = currentVariant.option3
      }
    } else {
      props[0]['pair'] = currentVariant.option2 === 'Matched Pair' ? 'true' : 'false'
      if (currentVariant.option2 === 'Matched Pair') {
        props[0].properties['pairId'] = '1'
        props[0].properties['pairType'] = 'Same'
      }
    }
    setPropertiesLocal(props)
  }, [currentVariant, selectedOptions])

  useEffect(() => {
    setShowWishlistSelector(isPdpSource)
  }, [isPdpSource, product.handle, currentVariant?.id])

  useEffect(() => {
    const handleOpen = () => {
      setShowWishlistSelector(isPdpSource)
    }

    document.addEventListener('open-wishlist-product-slideout', handleOpen)

    return () => {
      document.removeEventListener('open-wishlist-product-slideout', handleOpen)
    }
  }, [isPdpSource])

  const hasMultiLetters = product?.options?.includes('Number of Letters')
  const hasMultiStones = product?.options?.includes('Number of Stones')
  const isOnlyViewProduct = hasMultiLetters || hasMultiStones

  return (
    <div className="flex flex-col pb-8">
      {!isPdpSource && (
        <>
          <div className="flex items-stretch flex-nowrap justify-between mb-[5px]">
            <h3 className="h5 text-ink mb-0 mr-2.5">{product.title}</h3>
          </div>
          <p className="mb-10 text-sm-c font-light font-sans text-ink md:mb-[45px]">{handlePrice()}</p>
        </>
      )}
      {colours?.length > 0 && (
        <div className="colors-swatch-wrapper">
          <span className="colors-swatch__label">{wishlistDashboardState.product.material}: </span>
          <ul className="colors-swatch__list">
            {colours.map((colour) => (
              <li
                key={colour.name}
                className="colors-swatch__item cursor-pointer"
                style={{ order: colour.order }}
                onClick={() => setCurrentHandle(colour.productHandle)}
              >
                <span className={colour.productHandle === product.handle ? 'pb-0.5 border-b border-0 border-solid border-ink' : ''}>{colour.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {product.title.toLowerCase().includes('cartilage') && options.length > 0 && (
        <div className="colors-swatch-wrapper">
          <>
            {options.map((option) => (
              <Fragment key={option.name}>
                <span className="colors-swatch__label">{option.name}:</span>
                <ul className="colors-swatch__list">
                  {option.values.map((value) => (
                    <li key={value} className="colors-swatch__item cursor-pointer" onClick={() => setSelectedOptions({ [option.name]: value })}>
                      <span className={selectedOptions?.[option.name] === value ? 'pb-0.5 border-b border-0 border-solid border-ink' : ''}>{value}</span>
                    </li>
                  ))}
                </ul>
              </Fragment>
            ))}
          </>
        </div>
      )}
      <ProductVariant
        variants={product?.variants}
        options={product?.options}
        hasOnlyDefaultVariant={false}
        currentVariant={currentVariant}
        updateCurrentVariant={setCurrentVariant}
        hasPair={hasPair}
        disableGroupTopPadding
      ></ProductVariant>
      <div className="flex flex-col mt-5 lg:mt-10">
        {isOnlyViewProduct ? (
          <>
            <a href={product.url} className="Button Button--tertiary flex justify-center items-center w-full overflow-hidden">
              {wishlistDashboardState.viewProduct}
            </a>
            <p className="mt-3 text-sm font-calibre text-ink uppercase">{wishlistDashboardState.viewProductDescription}</p>
          </>
        ) : (
          <>
            {isPdpSource && <h4 className="h6 text-[14px] lg:text-[16px] mb-2 lg:mb-5 uppercase">{wishlistDashboardState.saveToWishlist}</h4>}
            {!isPdpSource && (
              <WishlistButton
                product={product}
                productBiLine={product.metafields.productBiLine}
                propertiesLocal={propertiesLocal}
                variantSelected={currentVariant}
                variantSelectedId={currentVariant.id}
                templateSuffix={templateSuffix}
                className="w-4 h-4"
                disableWishlist={''}
                errorMessage={''}
                isShowOnlyIcon={false}
                productPersonalisedPrice={productPersonalisedPrice}
                onRequestAdd={() => setShowWishlistSelector(true)}
              />
            )}
            {!isPdpSource && (
              <a href={product.url} className="mt-6 text-[14px] lg:text-[16px] uppercase text-ink border-0 border-b border-solid border-ink self-end">
                {wishlistDashboardState.viewProduct}
              </a>
            )}
            {showWishlistSelector && (
              <div className={classNames('mt-6 border-0 border-t border-solid border-grey-200', !isPdpSource && 'pt-6')}>
                <WishlistListPickerContent
                  product={product}
                  productBiline={product.metafields.productBiLine}
                  variantSelected={currentVariant}
                  templateSuffix={templateSuffix}
                  propertiesLocalInfo={propertiesLocal}
                  productPersonalisedPrice={productPersonalisedPrice}
                  modalId="wishlist-product-slideout"
                  showHeading={!isPdpSource}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
